import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  Plus,
  Eye,
  MessageSquare,
  FileText,
  AlertCircle,
  CheckCircle2,
  Clock,
  Zap,
  XCircle,
  User,
  Building2,
  Mail,
  Phone,
  AlertTriangle,
  PoundSterling,
  UserCheck,
  GitBranch,
  Pencil,
  Receipt,
  ArrowLeft,
  Home,
  ChevronRight,
  History,
  ArrowRightLeft,
  Edit,
  UserPlus,
  CreditCard,
  Minus,
  RotateCcw,
  Search,
  FileDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
} from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import { TransactionTypeBadge } from "@/components/transactions/transaction-type-badge";
import { formatCurrency } from "@/lib/utils/formatters";
import { cn } from "@/lib/utils";
import { useDisputesList, useDisputeById, useUpdateDispute, useQuickUpdateDisputes } from "@/hooks/useDisputesData";
import { useAddDisputeNote } from "@/hooks/useDisputeNotes";
import type { UpdateDisputeRequest } from "@/types/api/disputes";
import type { DisputeUI } from "@/types/ui/disputes";
import type { DisputeType } from "@/types/dispute-types";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";
import { LogCallModal, SendEmailModal } from "@/components/collections/action-modals";
import { TransactionDetailSheet } from "@/components/transactions/transaction-detail-sheet";
import { type Transaction, type TransactionLineItem } from "@/types/transaction";
import { useUsersList } from "@/hooks/useUsersData";
import { useCustomerContactsList } from "@/hooks/useCustomerContacts";
import { getWorkflowForDisputeType } from "@/lib/utils/dispute-workflow-mapper";
import { 
  getTotalLevelsForDisputeType, 
  getClosedReasonLabel,
  getResolutionOutcomeLabel,
  getResolutionOutcomeDescription,
  getDerivedStatus
} from "@/lib/utils/dispute-workflow-utils";
import { useDisputeTypesList } from "@/hooks/useDisputeTypesData";
import { ConsolidatedActivityTimeline } from "@/components/collections/consolidated-activity-timeline";
import type { CollectionActivity, CollectionCommunication, CaseNote, CaseAuditLog } from "@/types/collections";
import { DisputeDetailsSidebar } from "@/components/disputes/dispute-details-sidebar";
import { AddDisputeNoteModal } from '@/components/disputes/add-dispute-note-modal';
import { UploadDocumentModal } from '@/components/disputes/upload-document-modal';
import { toast } from 'sonner';

const statusColors = {
  "New": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "Under Review": "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  "In Progress": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Escalated": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  "Resolved": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  "Closed": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
};

const priorityColors = {
  "Low": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  "Medium": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "High": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Critical": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

const getAuditLogIcon = (action: CaseAuditLog['action']) => {
  switch (action) {
    case "status_change":
      return ArrowRightLeft;
    case "transaction_added":
      return Plus;
    case "transaction_removed":
      return Minus;
    case "priority_change":
      return AlertTriangle;
    case "assignment_change":
      return UserPlus;
    case "field_update":
      return Edit;
    case "note_added":
      return FileText;
    case "case_created":
      return History;
    default:
      return Clock;
  }
};

const getAuditLogColor = (action: CaseAuditLog['action']) => {
  switch (action) {
    case "status_change":
      return "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400";
    case "transaction_added":
      return "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400";
    case "transaction_removed":
      return "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400";
    case "priority_change":
      return "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400";
    case "assignment_change":
      return "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400";
    case "field_update":
      return "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400";
    case "note_added":
      return "bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400";
    case "case_created":
      return "bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400";
    default:
      return "bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400";
  }
};

// Helper to get badge variant for transaction status — mirrors case-details
function getTransactionStatusBadgeVariant(statusText: string): "destructive" | "default" | "secondary" {
  if (statusText === 'Overdue') return 'destructive';
  if (statusText === 'Completed') return 'default';
  return 'secondary'; // Outstanding
}

const DEFAULT_DISPUTE_TRANSACTION_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber',   label: 'Reference Number', visible: true,  order: 0,  locked: true },
  { id: 'transactionDate',   label: 'Transaction Date', visible: true,  order: 1  },
  { id: 'dueDate',           label: 'Due Date',          visible: true,  order: 2  },
  { id: 'customerName',      label: 'Customer Name',     visible: false, order: 3  },
  { id: 'transactionType',   label: 'Type',              visible: true,  order: 4  },
  { id: 'currency',          label: 'Currency',          visible: false, order: 5  },
  { id: 'amount',            label: 'Gross',             visible: true,  order: 6  },
  { id: 'outstandingAmount', label: 'Outstanding',       visible: true,  order: 7  },
  { id: 'status',            label: 'Status',            visible: true,  order: 8  },
  { id: 'description',       label: 'Description',       visible: false, order: 9  },
  { id: 'createdBy',         label: 'Created By',        visible: false, order: 10 },
  { id: 'modifiedBy',        label: 'Modified By',       visible: false, order: 11 },
  { id: 'accountCode',       label: 'Account',           visible: false, order: 12 },
  { id: 'netAmount',         label: 'Net',               visible: false, order: 13 },
  { id: 'vatAmount',         label: 'Tax',               visible: false, order: 14 },
  { id: 'onDispute',         label: 'On Dispute',        visible: false, order: 15 },
  { id: 'subLedger',         label: 'Sub Ledger',        visible: false, order: 16 },
  { id: 'orderNumber',       label: 'Order Number',      visible: false, order: 17 },
  { id: 'actions',           label: 'Actions',           visible: false, order: 18 },
];



// Helper to render workflow step icon
function formatDueDate(dueDate: string | undefined | null): string {
  if (dueDate) return format(new Date(dueDate), 'dd/MM/yyyy');
  return '-';
}

function renderStepIcon(stepLevel: number, currentLevel: number): React.ReactNode {
  if (stepLevel < currentLevel) {
    return <CheckCircle2 className="h-4 w-4" />;
  }
  if (stepLevel === currentLevel) {
    return <Clock className="h-4 w-4" />;
  }
  return <span className="text-sm">{stepLevel}</span>;
}

type DisputeTransaction = NonNullable<DisputeUI['transactions']>[number];

function getTransactionSortValues(
  column: string,
  a: DisputeTransaction,
  b: DisputeTransaction,
): [string | number, string | number] {
  switch (column) {
    case 'referenceNumber':   return [a.referenceNumber ?? '', b.referenceNumber ?? ''];
    case 'transactionDate':   return [a.date ?? '', b.date ?? ''];
    case 'dueDate':           return [a.dueDate ?? '', b.dueDate ?? ''];
    case 'description':       return [a.description ?? '', b.description ?? ''];
    case 'currency':          return [a.currency ?? '', b.currency ?? ''];
    case 'amount':            return [a.amount ?? 0, b.amount ?? 0];
    case 'status':            return [a.status ?? '', b.status ?? ''];
    case 'transactionType':   return [a.transactionType ?? '', b.transactionType ?? ''];
    case 'outstandingAmount': return [a.amount ?? 0, b.amount ?? 0];
    case 'netAmount':
    case 'vatAmount':         return [0, 0];
    default:                  return ['', ''];
  }
}

function getTransactionExportStatus(tx: DisputeTransaction): string {
  const isOverdue = tx.dueDate ? new Date(tx.dueDate) < new Date() : false;
  if (isOverdue) return 'Overdue';
  if (tx.status === 'completed') return 'Completed';
  return 'Outstanding';
}

function generateMockLineItems(amount: number): TransactionLineItem[] {
  const itemCount = Math.floor(Math.random() * 3) + 2;
  const lineItems: TransactionLineItem[] = [];
  let remainingAmount = amount;
  let totalSoFar = 0;

  const descriptions = [
    'Professional Services - Consulting',
    'Software License - Annual Subscription',
    'Hardware Equipment',
    'Maintenance & Support',
    'Training Services',
    'Implementation Services',
    'Cloud Storage - Monthly',
    'Technical Support',
  ];

  for (let i = 0; i < itemCount; i++) {
    const isLastItem = i === itemCount - 1;
    const quantity = Math.floor(Math.random() * 5) + 1;

    let subtotal: number;
    if (isLastItem) {
      subtotal = amount - totalSoFar;
    } else {
      subtotal = Math.floor(remainingAmount * (Math.random() * 0.4 + 0.2));
      remainingAmount -= subtotal;
    }

    const hasTax = !isLastItem && Math.random() > 0.5;
    const taxAmount = hasTax ? Math.round(subtotal * 0.1) : undefined;
    const total = subtotal + (taxAmount || 0);
    const unitPrice = Math.round(subtotal / quantity);

    totalSoFar += total;

    lineItems.push({
      id: i + 1,
      description: descriptions[Math.floor(Math.random() * descriptions.length)],
      quantity,
      unitPrice,
      subtotal,
      taxAmount,
      total,
    });
  }

  return lineItems;
}

function buildTransactionDetail(
  disputeTransaction: DisputeTransaction,
  customerId: number,
  customerName: string,
): Transaction {
  const amount = parseFloat(String(disputeTransaction.amount)) || 0;
  const lineItems = generateMockLineItems(amount);

  return {
    id: disputeTransaction.id as unknown as number,
    referenceNumber: disputeTransaction.referenceNumber,
    transactionDate: disputeTransaction.date,
    customerId,
    customerName,
    transactionType: disputeTransaction.transactionType,
    amount,
    currency: disputeTransaction.currency || 'GBP',
    status: disputeTransaction.status === 'outstanding' ? 'outstanding' : 'completed',
    description: disputeTransaction.description || '',
    createdBy: 'System',
    createdAt: disputeTransaction.date,
    dueDate: disputeTransaction.dueDate,
    lineItems,
  };
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
}

const ALL_EXPORT_COLUMNS = [
  { key: 'referenceNumber',   label: 'Reference Number' },
  { key: 'transactionDate',   label: 'Transaction Date' },
  { key: 'dueDate',           label: 'Due Date' },
  { key: 'customerName',      label: 'Customer Name' },
  { key: 'transactionType',   label: 'Type' },
  { key: 'currency',          label: 'Currency' },
  { key: 'amount',            label: 'Gross' },
  { key: 'outstandingAmount', label: 'Outstanding' },
  { key: 'status',            label: 'Status' },
  { key: 'description',       label: 'Description' },
  { key: 'createdBy',         label: 'Created By' },
  { key: 'modifiedBy',        label: 'Modified By' },
  { key: 'accountCode',       label: 'Account' },
  { key: 'netAmount',         label: 'Net' },
  { key: 'vatAmount',         label: 'Tax' },
  { key: 'onDispute',         label: 'On Dispute' },
  { key: 'subLedger',         label: 'Sub Ledger' },
  { key: 'orderNumber',       label: 'Order Number' },
];

function buildTransactionValueMap(tx: DisputeTransaction): Record<string, string | number> {
  return {
    referenceNumber:   tx.referenceNumber ?? '',
    transactionDate:   tx.date ? format(new Date(tx.date), 'dd/MM/yyyy') : '',
    dueDate:           tx.dueDate ? format(new Date(tx.dueDate), 'dd/MM/yyyy') : '',
    customerName:      '',
    transactionType:   tx.transactionType ?? '',
    currency:          tx.currency ?? '',
    amount:            tx.amount ?? 0,
    outstandingAmount: tx.amount ?? 0,
    status:            getTransactionExportStatus(tx),
    description:       tx.description ?? '',
    createdBy:         '',
    modifiedBy:        '',
    accountCode:       '',
    netAmount:         '',
    vatAmount:         '',
    onDispute:         'Yes',
    subLedger:         '',
    orderNumber:       '',
  };
}

function buildCsvContent(
  transactions: DisputeTransaction[],
  columnsToExport: { key: string; label: string }[],
): string {
  const header = columnsToExport.map((col) => `"${col.label}"`).join(',');
  const rows = transactions.map((tx) => {
    const valueMap = buildTransactionValueMap(tx);
    return columnsToExport.map((col) => {
      const value = valueMap[col.key] ?? '';
      return `"${String(value).replace(/"/g, '""')}"`;
    }).join(',');
  });
  return [header, ...rows].join('\n');
}

function filterAndSortTransactions(
  transactions: DisputeTransaction[],
  searchQuery: string,
  sortColumn: string | null,
  sortDirection: 'asc' | 'desc',
): DisputeTransaction[] {
  let result = [...transactions];

  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    result = result.filter((t) =>
      t.referenceNumber?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      t.currency?.toLowerCase().includes(q) ||
      t.status?.toLowerCase().includes(q)
    );
  }

  if (sortColumn) {
    result.sort((a, b) => {
      const [aVal, bVal] = getTransactionSortValues(sortColumn, a, b);
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  return result;
}

function computePageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const pages: (number | string)[] = [];
  const maxVisiblePages = 5;
  if (totalPages <= maxVisiblePages + 2) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push('ellipsis-start');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('ellipsis-end');
    pages.push(totalPages);
  }
  return pages;
}

function resolveWorkflowStages(
  dispute: DisputeUI | undefined,
  disputeTypes: DisputeType[],
): { level: number; title: string; description: string; estimatedDays?: number }[] {
  if (!dispute) return [];

  const disputeType = disputeTypes.find(dt => dt.name === dispute.type);

  if (disputeType?.workflow?.stages) {
    return [...disputeType.workflow.stages]
      .sort((a, b) => a.order - b.order)
      .map(stage => ({
        level: stage.order,
        title: stage.name,
        description: stage.description,
        estimatedDays: stage.properties?.slaHours ? Math.ceil(stage.properties.slaHours / 24) : undefined,
      }));
  }

  const fallbackWorkflow = getWorkflowForDisputeType(dispute.type);
  return fallbackWorkflow.stages;
}

const CLOSE_REASON_LABELS: Record<string, string> = {
  duplicate: 'Duplicate',
  withdrawn: 'Withdrawn by Customer',
  invalid: 'Invalid Dispute',
  other: 'Other',
};

function buildCloseDisputeNote(disputeId: string, closeReason: string, closeNote: string): CaseNote {
  const reasonLabel = CLOSE_REASON_LABELS[closeReason] ?? closeReason;
  const content = closeNote
    ? `Dispute closed - Reason: ${reasonLabel}\n\nNote: ${closeNote}`
    : `Dispute closed - Reason: ${reasonLabel}`;
  return {
    id: `note-${Date.now()}`,
    caseId: disputeId,
    createdBy: "John Smith",
    createdAt: new Date(),
    content,
  };
}

function validateResolveDispute(
  resolutionNotes: string,
  resolutionOutcome: string,
  resolutionAmount: string,
): string | null {
  if (!resolutionNotes.trim()) return "Please provide resolution notes";
  if (resolutionOutcome === 'partial' && !resolutionAmount.trim()) {
    return "Please provide resolution amount for partial resolution";
  }
  return null;
}

type ResolutionOutcome = 'upheld' | 'denied' | 'partial';

function buildResolveUpdate(
  dispute: DisputeUI,
  resolutionOutcome: ResolutionOutcome,
  resolutionAmount: string,
  resolutionNotes: string,
) {
  const isAtLevelNMinus1 = dispute.currentLevel === dispute.totalLevels - 1;
  const newLevel = isAtLevelNMinus1 ? dispute.totalLevels : dispute.currentLevel;
  return {
    resolutionOutcome,
    resolutionAmount: resolutionOutcome === 'partial' ? parseFloat(resolutionAmount) : null,
    resolutionDate: new Date().toISOString(),
    resolvedBy: "John Smith",
    resolutionDetails: resolutionNotes,
    resolvedAtLevel: dispute.currentLevel,
    currentLevel: newLevel,
  };
}

function computeNextSortState(
  currentColumn: string | null,
  currentDirection: 'asc' | 'desc',
  newColumn: string,
): { column: string; direction: 'asc' | 'desc' } {
  if (currentColumn === newColumn) {
    return { column: newColumn, direction: currentDirection === 'asc' ? 'desc' : 'asc' };
  }
  return { column: newColumn, direction: 'asc' };
}

function getDisputeTransactionList(
  dispute: DisputeUI | undefined,
  isEditMode: boolean,
  editedTransactions: DisputeTransaction[],
): DisputeTransaction[] {
  if (!dispute) return [];
  if (isEditMode) return editedTransactions;
  return dispute.transactions || [];
}

interface CloseDisputeParams {
  disputeId: string;
  closeReason: string;
  closeNote: string;
}

async function executeCloseDispute(
  params: CloseDisputeParams,
  queryClient: ReturnType<typeof import('@tanstack/react-query').useQueryClient>,
  refetchDisputes: () => void,
  setDisputeNotes: React.Dispatch<React.SetStateAction<CaseNote[]>>,
  setIsCloseDisputeModalOpen: (v: boolean) => void,
  setCloseReason: (v: 'duplicate' | 'customer_withdrew' | 'invalid_dispute' | 'other') => void,
  setCloseNote: (v: string) => void,
) {
  try {
    console.log("Closing dispute:", params);

    const store = getGlobalDataStore();
    store.updateDispute(params.disputeId, {
      closedReason: params.closeReason as 'duplicate' | 'withdrawn' | 'invalid' | 'other',
      closedDate: new Date().toISOString(),
      closedNote: params.closeNote,
    });

    const newNote = buildCloseDisputeNote(params.disputeId, params.closeReason, params.closeNote);
    setDisputeNotes(prev => [newNote, ...prev]);

    setIsCloseDisputeModalOpen(false);
    setCloseReason('duplicate');
    setCloseNote('');
    
    await queryClient.invalidateQueries({ queryKey: ['disputes'] });
    refetchDisputes();
    
    toast.success("Dispute closed successfully");
  } catch (error) {
    console.error("Failed to close dispute:", error);
    toast.error("Failed to close dispute");
  }
}

interface ResolveDisputeParams {
  disputeId: string;
  dispute: DisputeUI;
  resolutionOutcome: ResolutionOutcome;
  resolutionAmount: string;
  resolutionNotes: string;
}

async function executeResolveDispute(
  params: ResolveDisputeParams,
  queryClient: ReturnType<typeof import('@tanstack/react-query').useQueryClient>,
  refetchDisputes: () => void,
  setIsResolveDisputeModalOpen: (v: boolean) => void,
  setResolutionOutcome: (v: ResolutionOutcome) => void,
  setResolutionAmount: (v: string) => void,
  setResolutionNotes: (v: string) => void,
) {
  const validationError = validateResolveDispute(params.resolutionNotes, params.resolutionOutcome, params.resolutionAmount);
  if (validationError) {
    toast.error(validationError);
    return;
  }

  try {
    const update = buildResolveUpdate(params.dispute, params.resolutionOutcome, params.resolutionAmount, params.resolutionNotes);

    console.log("Resolving dispute:", {
      disputeId: params.disputeId,
      resolutionOutcome: params.resolutionOutcome,
      resolutionAmount: params.resolutionAmount,
      resolvedAtLevel: params.dispute.currentLevel,
      newLevel: update.currentLevel,
    });

    const store = getGlobalDataStore();
    store.updateDispute(params.disputeId, update);

    setIsResolveDisputeModalOpen(false);
    setResolutionOutcome('upheld');
    setResolutionAmount('');
    setResolutionNotes('');
    
    await queryClient.invalidateQueries({ queryKey: ['disputes'] });
    refetchDisputes();
    
    toast.success("Dispute resolved successfully");
  } catch (error) {
    console.error("Failed to resolve dispute:", error);
    toast.error("Failed to resolve dispute");
  }
}

interface ReopenDisputeParams {
  disputeId: string;
  reopenReason: string;
  previousClosedReason: string | null | undefined;
}

async function executeReopenDispute(
  params: ReopenDisputeParams,
  queryClient: ReturnType<typeof import('@tanstack/react-query').useQueryClient>,
  refetchDisputes: () => void,
  setIsReopenDisputeModalOpen: (v: boolean) => void,
  setReopenReason: (v: string) => void,
) {
  if (!params.reopenReason.trim()) {
    toast.error("Please provide a reason for reopening the dispute");
    return;
  }

  try {
    console.log("Reopening dispute:", {
      disputeId: params.disputeId,
      reason: params.reopenReason,
      previousClosedReason: params.previousClosedReason,
    });

    const store = getGlobalDataStore();
    store.updateDispute(params.disputeId, {
      closedReason: null,
      closedDate: null,
      closedNote: null,
    });

    setIsReopenDisputeModalOpen(false);
    setReopenReason('');
    
    await queryClient.invalidateQueries({ queryKey: ['disputes'] });
    refetchDisputes();
    
    toast.success("Dispute reopened successfully");
  } catch (error) {
    console.error("Failed to reopen dispute:", error);
    toast.error("Failed to reopen dispute");
  }
}

interface ConfirmSaveParams {
  selectedDispute: DisputeUI;
  editedDispute: Partial<DisputeUI>;
  editedTransactions: DisputeTransaction[];
  selectedTxIds: Set<string>;
  removedTransactionsLength: number;
  updateDisputeMutation: { mutateAsync: (data: UpdateDisputeRequest) => Promise<unknown> };
  setShowSaveConfirm: (v: boolean) => void;
  setIsEditMode: (v: boolean) => void;
  setSelectedTxIds: (v: Set<string>) => void;
  refetchDisputes: () => void;
}

async function executeConfirmSave(params: ConfirmSaveParams) {
  params.setShowSaveConfirm(false);

  try {
    const linkedTransactions = params.editedTransactions.filter(t => params.selectedTxIds.has(t.id));

    const request: UpdateDisputeRequest = {
      contactId: params.editedDispute.contactId,
    };

    await params.updateDisputeMutation.mutateAsync(request);

    params.setIsEditMode(false);
    params.setSelectedTxIds(new Set());

    console.log("Saving edited dispute:", {
      ...params.editedDispute,
      transactions: linkedTransactions,
      removedTransactionCount: params.removedTransactionsLength,
    });

    params.refetchDisputes();
  } catch (error) {
    console.error("Failed to save dispute:", error);
  }
}

function getTransactionStatusText(transaction: { dueDate: string; status: string }): string {
  if (transaction.status === 'completed') return 'Completed';
  if (transaction.dueDate && new Date(transaction.dueDate) < new Date()) return 'Overdue';
  return 'Outstanding';
}

function handleExportTransactionsAction(
  filteredSortedTransactions: DisputeTransaction[],
  exportAll: boolean,
  txColumnVisibility: Record<string, boolean>,
  disputeId: string | undefined,
) {
  if (filteredSortedTransactions.length === 0) {
    toast.error('No transactions to export');
    return;
  }
  const columnsToExport = exportAll
    ? ALL_EXPORT_COLUMNS
    : ALL_EXPORT_COLUMNS.filter((col) => txColumnVisibility[col.key]);

  const csv = buildCsvContent(filteredSortedTransactions, columnsToExport);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `dispute-${disputeId ?? 'unknown'}-transactions-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  toast.success(`Exported ${filteredSortedTransactions.length} transaction(s)`);
}

function toggleSetItem<T>(set: Set<T>, item: T): Set<T> {
  const next = new Set(set);
  if (next.has(item)) {
    next.delete(item);
  } else {
    next.add(item);
  }
  return next;
}

function enterEditMode(
  selectedDispute: DisputeUI,
  setEditedDispute: (v: Partial<DisputeUI>) => void,
  setEditedTransactions: (v: DisputeTransaction[]) => void,
  setSelectedTxIds: (v: Set<string>) => void,
) {
  setEditedDispute({
    contactId: selectedDispute.contactId,
    contactName: selectedDispute.contactName,
    contactEmail: selectedDispute.contactEmail,
    contactPhone: selectedDispute.contactPhone,
    status: selectedDispute.status,
    priority: selectedDispute.priority,
    assignedTo: selectedDispute.assignedTo,
  });
  const txList = selectedDispute.transactions || [];
  setEditedTransactions(txList);
  setSelectedTxIds(new Set(txList.map(t => t.id)));
}

function exitEditMode(
  setEditedDispute: (v: Partial<DisputeUI>) => void,
  setEditedTransactions: (v: DisputeTransaction[]) => void,
  setSelectedTxIds: (v: Set<string>) => void,
) {
  setEditedDispute({});
  setEditedTransactions([]);
  setSelectedTxIds(new Set());
}

function syncCustomerContacts(
  selectedDispute: DisputeUI | undefined,
  selectedDisputeContacts: Array<{ id: string; firstName: string; surname: string; email: string; phone: string }>,
  setCustomerContacts: (v: Array<{ id: string; name: string; email: string; phone: string }>) => void,
) {
  if (selectedDispute && selectedDisputeContacts.length > 0) {
    const newContacts = selectedDisputeContacts.map(c => ({
      id: c.id,
      name: `${c.firstName} ${c.surname}`,
      email: c.email,
      phone: c.phone
    }));
    setCustomerContacts(newContacts);
  }
}

function buildDocumentActivityDescription(fileName: string, description: string): string {
  if (description) return `Document uploaded: ${fileName} - ${description}`;
  return `Document uploaded: ${fileName}`;
}

// proceedToNextStageAction has been replaced by inline handleProceedToNextStage in the component

function getDisputeNumericId(dispute: DisputeUI | undefined): number {
  if (!dispute) return 0;
  return dispute.numericId ?? parseInt(dispute.id.replace(/\D/g, ""), 10);
}

function computeTotalPages(totalItems: number, itemsPerPage: number): number {
  if (totalItems <= 0) return 0;
  return Math.ceil(totalItems / itemsPerPage);
}

function computeDisputeUIState(selectedDispute: DisputeUI | undefined) {
  if (!selectedDispute) {
    return {
      derivedStatus: "New" as string,
      isNewOrInProgress: false as false | DisputeUI,
      showNextStageButton: false,
      showResolveDisputeButton: false as false | DisputeUI,
      showCloseDisputeButton: false as false | DisputeUI,
      showReopenDisputeButton: false,
      isDisputeLocked: false,
    };
  }

  const derivedStatus = getDerivedStatus(selectedDispute.currentLevel, selectedDispute.totalLevels, selectedDispute.closedReason);
  const isNewOrInProgress = !selectedDispute.resolutionOutcome && !selectedDispute.closedReason && selectedDispute;
  const showNextStageButton = isNewOrInProgress && selectedDispute.currentLevel < selectedDispute.totalLevels;
  const showResolveDisputeButton = isNewOrInProgress;
  const showCloseDisputeButton = isNewOrInProgress;
  const showReopenDisputeButton = !!selectedDispute.closedReason && !selectedDispute.resolutionOutcome;
  const isDisputeLocked = !!selectedDispute.resolutionOutcome || !!selectedDispute.closedReason;

  return {
    derivedStatus,
    isNewOrInProgress,
    showNextStageButton,
    showResolveDisputeButton,
    showCloseDisputeButton,
    showReopenDisputeButton,
    isDisputeLocked,
  };
}


function getSidebarWidthClass(collapsed: boolean): string {
  if (collapsed) return "w-0";
  return "w-80";
}

function getEditButtonLabel(isEditMode: boolean): string {
  if (isEditMode) return "Cancel";
  return "Edit Dispute";
}

// Mock data for Activity Timeline
const mockActivities: CollectionActivity[] = [
  {
    id: "act-1",
    caseId: "CASE-001",
    type: "status_change",
    description: "Dispute status changed from 'New' to 'In Progress'",
    outcome: "Status updated successfully",
    createdBy: "System",
    createdAt: new Date("2024-01-15T09:00:00"),
  },
  {
    id: "act-2",
    caseId: "CASE-001",
    type: "call",
    description: "Called customer to discuss dispute details",
    outcome: "Customer provided additional documentation",
    createdBy: "John Smith",
    createdAt: new Date("2024-01-16T10:30:00"),
  },
];

const mockCommunications: CollectionCommunication[] = [
  {
    id: "comm-1",
    caseId: "CASE-001",
    type: "email",
    direction: "outbound",
    subject: "Dispute Investigation - Additional Information Required",
    content: "We are investigating your dispute and require additional documentation to proceed.",
    sentBy: "Sarah Johnson",
    sentAt: new Date("2024-01-15T14:15:00"),
    status: "sent",
  },
  {
    id: "comm-2",
    caseId: "CASE-001",
    type: "email",
    direction: "inbound",
    subject: "RE: Dispute Investigation",
    content: "Please find attached the requested documentation for your review.",
    sentBy: "Customer",
    sentAt: new Date("2024-01-16T09:00:00"),
    status: "delivered",
  },
];

const mockNotes: CaseNote[] = [
  {
    id: "note-1",
    caseId: "CASE-001",
    content: "Contacted customer to verify service dates. Awaiting response.",
    createdBy: "John Smith",
    createdAt: new Date("2024-01-16T10:30:00"),
  },
];

const mockDocuments = [
  { name: "Original Invoice.pdf", size: "245 KB", date: "2024-01-15" },
  { name: "Service Agreement.pdf", size: "1.2 MB", date: "2024-01-15" },
  { name: "Customer Email.msg", size: "45 KB", date: "2024-01-16" },
];

const mockAuditLogs: CaseAuditLog[] = [
  {
    id: "audit-1",
    caseId: "DSP-001",
    action: "case_created",
    description: "Dispute case created",
    oldValue: null,
    newValue: "New",
    createdBy: "System",
    createdAt: new Date("2024-01-15T09:00:00"),
  },
  {
    id: "audit-2",
    caseId: "DSP-001",
    action: "assignment_change",
    description: "Dispute assigned to team member",
    oldValue: "Unassigned",
    newValue: "John Smith",
    createdBy: "Sarah Johnson",
    createdAt: new Date("2024-01-15T09:15:00"),
  },
  {
    id: "audit-3",
    caseId: "DSP-001",
    action: "status_change",
    description: "Dispute status changed",
    oldValue: "New",
    newValue: "In Progress",
    createdBy: "John Smith",
    createdAt: new Date("2024-01-15T10:00:00"),
  },
  {
    id: "audit-4",
    caseId: "DSP-001",
    action: "note_added",
    description: "Note added to dispute",
    oldValue: null,
    newValue: "Contacted customer to verify service dates",
    createdBy: "John Smith",
    createdAt: new Date("2024-01-16T10:30:00"),
  },
];

export default function DisputeDetailsPage() {
  const { disputeId } = useParams<{ disputeId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "all";

  const [logCallOpen, setLogCallOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isNextActionModalOpen, setIsNextActionModalOpen] = useState(false);
  const [isCloseDisputeModalOpen, setIsCloseDisputeModalOpen] = useState(false);
  const [closeReason, setCloseReason] = useState<'duplicate' | 'customer_withdrew' | 'invalid_dispute' | 'other'>('duplicate');
  const [closeNote, setCloseNote] = useState('');
  
  // Resolution modal state
  const [isResolveDisputeModalOpen, setIsResolveDisputeModalOpen] = useState(false);
  const [resolutionOutcome, setResolutionOutcome] = useState<ResolutionOutcome>('upheld');
  const [resolutionAmount, setResolutionAmount] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  
  // Reopen dispute modal state
  const [isReopenDisputeModalOpen, setIsReopenDisputeModalOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  
  const [nextActionNotes, setNextActionNotes] = useState('');
  const [nextActionFollowUpDate, setNextActionFollowUpDate] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  
  // Transaction detail sheet state
  const [transactionDetailSheetOpen, setTransactionDetailSheetOpen] = useState(false);
  const [selectedTransactionDetail, setSelectedTransactionDetail] = useState<Transaction | null>(null);
  

  // Edit mode state
  const [editedDispute, setEditedDispute] = useState<Partial<DisputeUI>>({});
  const [customerContacts, setCustomerContacts] = useState<Array<{ id: string; name: string; email: string; phone: string }>>([]);
  const [editedTransactions, setEditedTransactions] = useState<Array<{
    id: string;
    referenceNumber: string;
    date: string;
    dueDate: string;
    description: string;
    transactionType: import('@/types/transaction').TransactionType;
    amount: number;
    currency: string;
    status: 'outstanding' | 'overdue' | 'completed';
  }>>([]);
  const [selectedTxIds, setSelectedTxIds] = useState<Set<string>>(new Set());
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [, setEditForm] = useState({
    latestNote: "",
  });

  // Notes and Activities state
  const [disputeNotes, setDisputeNotes] = useState<CaseNote[]>(mockNotes);
  const [activities, setActivities] = useState<CollectionActivity[]>(mockActivities);
  const [auditLogs] = useState<CaseAuditLog[]>(mockAuditLogs);
  
  // Documents state
  const [documents, setDocuments] = useState(mockDocuments);
  const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] = useState(false);

  // Fetch data
  const queryClient = useQueryClient();
  const { data: selectedDispute, isLoading: isLoadingDispute, refetch: refetchDispute } = useDisputeById(disputeId);
  // Keep the list query for the sidebar
  const { data: disputesResult, refetch: refetchDisputes } = useDisputesList();
  const disputes = disputesResult?.disputes ?? [];
  const { data: users = [] } = useUsersList();
  const { data: disputeTypes = [] } = useDisputeTypesList();
  const selectedDisputeNumericId = getDisputeNumericId(selectedDispute);
  const updateDisputeMutation = useUpdateDispute(selectedDisputeNumericId);
  const quickUpdateDisputes = useQuickUpdateDisputes();
  const addDisputeNoteMutation = useAddDisputeNote();

  const workflowStages = resolveWorkflowStages(selectedDispute, disputeTypes);

  // Fetch customer contacts
  const customerId = selectedDispute?.customerId?.toString() || "0";
  const { data: selectedDisputeContacts = [] } = useCustomerContactsList(customerId);

  // Sync customer contacts when dispute is selected
  useEffect(() => {
    syncCustomerContacts(selectedDispute, selectedDisputeContacts, setCustomerContacts);
  }, [selectedDispute?.id, selectedDisputeContacts]);

  // Initialize edited dispute when entering edit mode
  useEffect(() => {
    if (isEditMode && selectedDispute) {
      enterEditMode(selectedDispute, setEditedDispute, setEditedTransactions, setSelectedTxIds);
    } else {
      exitEditMode(setEditedDispute, setEditedTransactions, setSelectedTxIds);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, selectedDispute?.id]);

  const handleEditFormChange = (field: string, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleTx = (txId: string) => {
    setSelectedTxIds(toggleSetItem(selectedTxIds, txId));
  };

  // Derived: transactions that will be removed on save
  const removedTransactions = editedTransactions.filter(t => !selectedTxIds.has(t.id));

  // Called when the user clicks "Save" — shows confirmation if any transactions were unchecked
  const handleSaveEditedDispute = () => {
    if (removedTransactions.length > 0) {
      setShowSaveConfirm(true);
    } else {
      handleConfirmSave();
    }
  };

  // The actual commit — called either directly (no removals) or after confirmation
  const handleConfirmSave = async () => {
    if (!selectedDispute) return;
    await executeConfirmSave({
      selectedDispute, editedDispute, editedTransactions, selectedTxIds,
      removedTransactionsLength: removedTransactions.length, updateDisputeMutation, setShowSaveConfirm, setIsEditMode, setSelectedTxIds, refetchDisputes: () => { refetchDispute(); refetchDisputes(); },
    });
  };


  const handleNextAction = () => {
    setIsNextActionModalOpen(true);
  };

  const handleAddNote = () => {
    setIsAddNoteModalOpen(true);
  };

  const handleCloseDispute = async () => {
    if (!selectedDispute) return;
    if (closeReason === 'other' && !closeNote.trim()) return;

    const closeReasonLabels: Record<string, string> = {
      duplicate: 'Duplicate Entry',
      customer_withdrew: 'Customer Withdrew Dispute',
      invalid_dispute: 'Invalid Dispute',
      other: 'Other',
    };
    const composedCloseNote = closeNote
      ? `Dispute closed - Reason: ${closeReasonLabels[closeReason]}\n\nNote: ${closeNote}`
      : `Dispute closed - Reason: ${closeReasonLabels[closeReason]}`;

    try {
      await quickUpdateDisputes.mutateAsync([{
        disputeStringId: selectedDispute.id,
        numericId: selectedDisputeNumericId || undefined,
        status: "Closed",
        closedReason: closeReason,
        closedDate: new Date().toISOString().split("T")[0],
        closedNote: composedCloseNote,
      }]);

      // Also add a note via the API
      if (selectedDisputeNumericId) {
        await addDisputeNoteMutation.mutateAsync({
          disputeId: selectedDisputeNumericId.toString(),
          content: composedCloseNote,
          userId: "0",
          userName: "Current User",
        });
      }

      setIsCloseDisputeModalOpen(false);
      setCloseReason('duplicate');
      setCloseNote('');

      toast.success("Dispute closed successfully");
      refetchDispute();
      refetchDisputes();
    } catch (error) {
      console.error("Failed to close dispute:", error);
      toast.error("Failed to close dispute");
    }
  };

  const handleResolveDispute = async () => {
    if (!selectedDispute) return;

    const validationError = validateResolveDispute(resolutionNotes, resolutionOutcome, resolutionAmount);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      await quickUpdateDisputes.mutateAsync([{
        disputeStringId: selectedDispute.id,
        numericId: selectedDisputeNumericId || undefined,
        status: "Resolved",
        resolutionNotes: resolutionNotes,
        resolutionOutcome: resolutionOutcome,
        resolutionAmount: resolutionOutcome === 'partial' ? parseFloat(resolutionAmount) : selectedDispute.amount ?? null,
        resolutionDate: new Date().toISOString().split("T")[0],
        resolvedBy: "Current User",
      }]);

      setIsResolveDisputeModalOpen(false);
      setResolutionOutcome('upheld');
      setResolutionAmount('');
      setResolutionNotes('');

      toast.success("Dispute resolved successfully");
      refetchDispute();
      refetchDisputes();
    } catch (error) {
      console.error("Failed to resolve dispute:", error);
      toast.error("Failed to resolve dispute");
    }
  };

  const handleReopenDispute = async () => {
    if (!selectedDispute) return;
    if (!reopenReason.trim()) {
      toast.error("Please provide a reason for reopening the dispute");
      return;
    }

    try {
      await quickUpdateDisputes.mutateAsync([{
        disputeStringId: selectedDispute.id,
        numericId: selectedDisputeNumericId || undefined,
        status: selectedDispute.currentLevel === 1 ? "New" : "In Progress",
        closedReason: "",
        closedNote: "",
      }]);

      // Also add a note via the API with the reopen reason
      if (selectedDisputeNumericId) {
        const reopenNote = `Dispute reopened - Reason: ${reopenReason}${selectedDispute.closedReason ? `\nPrevious close reason: ${selectedDispute.closedReason}` : ''}`;
        await addDisputeNoteMutation.mutateAsync({
          disputeId: selectedDisputeNumericId.toString(),
          content: reopenNote,
          userId: "0",
          userName: "Current User",
        });
      }

      setIsReopenDisputeModalOpen(false);
      setReopenReason('');

      toast.success("Dispute reopened successfully");
      refetchDispute();
      refetchDisputes();
    } catch (error) {
      console.error("Failed to reopen dispute:", error);
      toast.error("Failed to reopen dispute");
    }
  };

  const handleUploadDocument = () => {
    setIsUploadDocumentModalOpen(true);
  };

  const handleViewTransactionDetail = (disputeTransaction: DisputeTransaction) => {
    const transaction = buildTransactionDetail(
      disputeTransaction,
      selectedDispute?.customerId || 0,
      selectedDispute?.customer || '',
    );
    setSelectedTransactionDetail(transaction);
    setTransactionDetailSheetOpen(true);
  };


  const handleDocumentUpload = (files: File[], description: string) => {
    // Create new document entries
    const newDocuments = files.map((file) => ({
      name: file.name,
      size: formatFileSize(file.size),
      date: new Date().toISOString().split('T')[0],
    }));

    // Add to documents list
    setDocuments((prev) => [...newDocuments, ...prev]);

    // Log activity for each document
    files.forEach((file) => {
      const newActivity: CollectionActivity = {
        id: `act-${Date.now()}-${Math.random()}`,
        caseId: disputeId || '',
        type: 'note',
        description: buildDocumentActivityDescription(file.name, description),
        outcome: 'Document added to dispute',
        createdBy: 'Current User',
        createdAt: new Date(),
      };

      setActivities((prev) => [newActivity, ...prev]);
    });
  };

  const handleSaveNote = async (noteContent: string) => {
    if (selectedDisputeNumericId) {
      try {
        await addDisputeNoteMutation.mutateAsync({
          disputeId: selectedDisputeNumericId.toString(),
          content: noteContent,
          userId: "0",
          userName: "Current User",
        });
        toast.success("Note added successfully");
        // Refetch to update the UI
        refetchDispute();
        refetchDisputes();
      } catch {
        toast.error("Failed to add note");
      }
    } else {
      // Fallback for mock/demo mode — keep existing local state behavior
      const newNote: CaseNote = {
        id: `note-${Date.now()}`,
        caseId: disputeId || "",
        content: noteContent,
        createdBy: "Current User",
        createdAt: new Date(),
      };
      setDisputeNotes(prev => [newNote, ...prev]);
      const newActivity: CollectionActivity = {
        id: `activity-${Date.now()}`,
        caseId: disputeId || "",
        type: "note" as const,
        description: "Note added",
        outcome: noteContent,
        createdBy: "Current User",
        createdAt: new Date(),
      };
      setActivities(prev => [newActivity, ...prev]);
      toast.success("Note added successfully");
    }
  };

  const handleProceedToNextStage = async () => {
    if (!nextActionNotes.trim()) {
      toast.error('Notes are required');
      return;
    }
    if (!selectedDispute) {
      toast.error('No dispute selected');
      return;
    }

    const newLevel = selectedDispute.currentLevel + 1;
    const request: UpdateDisputeRequest = {
      currentLevel: newLevel,
    };

    // If the new level equals totalLevels, set status to Resolved (statusId = 5)
    if (newLevel === selectedDispute.totalLevels) {
      request.statusId = 5;
    }

    try {
      await updateDisputeMutation.mutateAsync(request);

      // Log activity
      const nextStage = workflowStages.find(s => s.level === newLevel);
      const stageLabel = nextStage?.title || 'Level ' + String(newLevel);
      const newActivity: CollectionActivity = {
        id: `act-${Date.now()}`,
        caseId: selectedDispute.id,
        type: "workflow_progress",
        description: `Advanced to workflow stage: ${stageLabel}`,
        outcome: nextActionNotes,
        createdBy: "Current User",
        createdAt: new Date(),
      };
      setActivities(prev => [newActivity, ...prev]);

      toast.success(
        newLevel === selectedDispute.totalLevels
          ? 'Dispute resolved - all workflow stages completed'
          : `Successfully moved to: ${stageLabel}`
      );

      // Close modal and reset
      setIsNextActionModalOpen(false);
      setNextActionNotes('');
      setNextActionFollowUpDate('');

      // Refresh dispute data
      refetchDispute();
      refetchDisputes();
    } catch (error) {
      console.error('Failed to proceed to next stage:', error);
      toast.error('Failed to advance to next stage');
    }
  };

  const handleCancelNextAction = () => {
    setIsNextActionModalOpen(false);
    setNextActionNotes('');
    setNextActionFollowUpDate('');
  };

  const transactionList = getDisputeTransactionList(
    selectedDispute,
    isEditMode,
    editedTransactions as DisputeTransaction[],
  );

  // ── Transactions table: search / sort / pagination / column config ──────────
  const [txSearchQuery, setTxSearchQuery] = useState('');
  const [txSortColumn, setTxSortColumn] = useState<string | null>(null);
  const [txSortDirection, setTxSortDirection] = useState<'asc' | 'desc'>('asc');
  const [txCurrentPage, setTxCurrentPage] = useState(1);
  const [txItemsPerPage, setTxItemsPerPage] = useState(25);

  const {
    columns: txColumns,
    savePreferences: saveTxColumnPreferences,
    resetToDefaults: resetTxColumnPreferences,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.DisputeDetailsTransactions,
    DEFAULT_DISPUTE_TRANSACTION_COLUMNS,
  );

  const txColumnVisibility = useMemo(() => {
    const vis: Record<string, boolean> = {};
    txColumns.forEach((col) => { vis[col.id] = col.visible; });
    return vis;
  }, [txColumns]);

  const filteredSortedTransactions = useMemo(
    () => filterAndSortTransactions(transactionList, txSearchQuery, txSortColumn, txSortDirection),
    [transactionList, txSearchQuery, txSortColumn, txSortDirection],
  );

  const txTotalItems = filteredSortedTransactions.length;
  const txTotalPages = computeTotalPages(txTotalItems, txItemsPerPage);
  const txPagedTransactions = useMemo(() => {
    const start = (txCurrentPage - 1) * txItemsPerPage;
    return filteredSortedTransactions.slice(start, start + txItemsPerPage);
  }, [filteredSortedTransactions, txCurrentPage, txItemsPerPage]);

  const getTxPageNumbers = (): (number | string)[] => computePageNumbers(txCurrentPage, txTotalPages);

  const handleTxSort = (column: string) => {
    const next = computeNextSortState(txSortColumn, txSortDirection, column);
    setTxSortColumn(next.column);
    setTxSortDirection(next.direction);
    setTxCurrentPage(1);
  };

  const renderTxSortIcon = (column: string) => {
    if (txSortColumn !== column) return <ArrowUpDown className="ml-2 h-4 w-4" />;
    return txSortDirection === 'asc'
      ? <ArrowUp className="ml-2 h-4 w-4" />
      : <ArrowDown className="ml-2 h-4 w-4" />;
  };

  const handleTxColumnConfigApply = (columns: ColumnDefinition[]) => {
    saveTxColumnPreferences(columns);
    toast.success('Column configuration updated');
  };

  const handleExportTransactions = (exportAll: boolean) => {
    handleExportTransactionsAction(filteredSortedTransactions, exportAll, txColumnVisibility, selectedDispute?.id);
  };
  // ── End transactions table helpers ──────────────────────────────────────────

  // Calculate derived status and button visibility
  const {
    derivedStatus,
    isNewOrInProgress,
    showNextStageButton,
    showResolveDisputeButton,
    showCloseDisputeButton,
    showReopenDisputeButton,
    isDisputeLocked,
  } = computeDisputeUIState(selectedDispute);

  console.log("Dispute Status Debug:", {
    disputeId: selectedDispute?.id,
    derivedStatus,
    closedReason: selectedDispute?.closedReason,
    resolutionOutcome: selectedDispute?.resolutionOutcome,
    isNewOrInProgress,
    isDisputeLocked,
  });

  if (isLoadingDispute) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!selectedDispute) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Dispute Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The dispute you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate('/disputes/all')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to All Disputes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Left Sidebar */}
      <div className={cn(
        "relative transition-all duration-300 ease-in-out shrink-0",
        getSidebarWidthClass(isSidebarCollapsed)
      )}>
        <DisputeDetailsSidebar 
          disputes={disputes}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Vertical Tab/Handle - Only visible when sidebar is collapsed */}
        {isSidebarCollapsed && (
          <button
            onClick={() => setIsSidebarCollapsed(false)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-40 group"
            title="Show Disputes"
          >
            <div className="flex items-center bg-teal-500 hover:bg-teal-600 text-white shadow-lg rounded-r-lg transition-all duration-200 group-hover:pr-2">
              <div className="flex flex-col items-center justify-center py-4 px-2 gap-1">
                <ChevronRight className="h-5 w-5" />
                <div className="writing-mode-vertical text-xs font-medium tracking-wider">
                  DISPUTES
                </div>
              </div>
            </div>
          </button>
        )}
        
        <div className="flex-1 overflow-auto">
          <div className="space-y-6 p-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="h-8 gap-1 px-2"
        >
          <Home className="h-4 w-4" />
        </Button>
        <ChevronRight className="h-4 w-4" />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/disputes/all')}
          className="h-8 px-2"
        >
          All Disputes
        </Button>
        <ChevronRight className="h-4 w-4" />
        <span className="font-medium text-foreground">{selectedDispute.id}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(from === "dashboard" ? '/disputes' : '/disputes/all')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Dispute Details - {selectedDispute.id}</h1>
              <p className="text-muted-foreground">View and manage dispute information</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditMode && (
            <Button
              size="sm"
              onClick={handleSaveEditedDispute}
              className="gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              Save Changes
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditMode(!isEditMode)}
            className="gap-2"
            disabled={isDisputeLocked}
          >
            <Pencil className="h-4 w-4" />
            {getEditButtonLabel(isEditMode)}
          </Button>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <Card className="bg-gradient-to-r from-teal-50 to-blue-50 dark:from-teal-950 dark:to-blue-950 border-teal-200 dark:border-teal-800">
        <CardContent className="py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              <span className="font-semibold text-sm">Quick Actions</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLogCallOpen(true)}
                className="gap-2 bg-white dark:bg-gray-900"
                disabled={isDisputeLocked}
              >
                <Phone className="h-4 w-4" />
                Call Customer
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSendEmailOpen(true)}
                className="gap-2 bg-white dark:bg-gray-900"
                disabled={isDisputeLocked}
              >
                <Mail className="h-4 w-4" />
                Send Email
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddNote}
                className="gap-2 bg-white dark:bg-gray-900"
                disabled={isDisputeLocked}
              >
                <MessageSquare className="h-4 w-4" />
                Add Note
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleUploadDocument}
                className="gap-2 bg-white dark:bg-gray-900"
                disabled={isDisputeLocked}
              >
                <Plus className="h-4 w-4" />
                Upload Document
              </Button>
              <Separator orientation="vertical" className="h-6" />
              
              {/* Next Stage Button - Only show if not at final level and not resolved/closed */}
              {showNextStageButton && (
                <Button
                  size="sm"
                  onClick={handleNextAction}
                  className="gap-2 bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Next Stage
                </Button>
              )}

              {/* Resolve Dispute Button - Show for New/In Progress only */}
              {showResolveDisputeButton && (
                <Button
                  size="sm"
                  onClick={() => setIsResolveDisputeModalOpen(true)}
                  className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Resolve Dispute
                </Button>
              )}

              {/* Close Dispute Button - Show for New/In Progress only */}
              {showCloseDisputeButton && (
                <Button
                  size="sm"
                  onClick={() => setIsCloseDisputeModalOpen(true)}
                  className="gap-2 bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <XCircle className="h-4 w-4" />
                  Close Dispute
                </Button>
              )}

              {/* Reopen Dispute Button - Only show for Closed disputes */}
              {showReopenDisputeButton && (
                <Button
                  size="sm"
                  onClick={() => setIsReopenDisputeModalOpen(true)}
                  className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reopen Dispute
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="workflow">Workflow</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        {/* Tab 1: Overview */}
        <TabsContent value="overview" className="space-y-3">
          {/* Top Row: Customer Information and Dispute Summary */}
          <div className="grid gap-3 md:grid-cols-2">
            {/* Customer Information Panel */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <User className="h-4 w-4 text-primary" />
                    Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      Customer
                    </p>
                    <p className="text-sm font-semibold">
                      {selectedDispute.customerAccountCode} - {selectedDispute.customer}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Contact Name
                    </p>
                    {isEditMode ? (
                      <Select
                        value={String(editedDispute.contactId ?? selectedDispute.contactId ?? "")}
                        onValueChange={(value) => {
                          const contact = customerContacts.find(c => String(c.id) === value);
                          setEditedDispute(prev => ({
                            ...prev,
                            contactId: contact ? Number(contact.id) : prev.contactId,
                            contactName: contact?.name || prev.contactName,
                            contactEmail: contact?.email || prev.contactEmail,
                            contactPhone: contact?.phone || prev.contactPhone,
                          }));
                        }}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder={editedDispute.contactName || selectedDispute.contactName || "Select contact"} />
                        </SelectTrigger>
                        <SelectContent>
                          {customerContacts.map((contact) => (
                            <SelectItem key={String(contact.id)} value={String(contact.id)}>
                              {contact.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm font-semibold">{selectedDispute.contactName}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      Email
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => setSendEmailOpen(true)}
                        className="gap-2 bg-teal-600 hover:bg-teal-700 text-white h-7 px-2 shrink-0"
                        disabled={isDisputeLocked}
                      >
                        <Mail className="h-3.5 w-3.5" />
                      </Button>
                      <p className="text-sm font-semibold break-all">{isEditMode ? (editedDispute.contactEmail ?? selectedDispute.contactEmail) : selectedDispute.contactEmail}</p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      Phone
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => setLogCallOpen(true)}
                        className="gap-2 bg-teal-600 hover:bg-teal-700 text-white h-7 px-2 shrink-0"
                        disabled={isDisputeLocked}
                      >
                        <Phone className="h-3.5 w-3.5" />
                      </Button>
                      <p className="text-sm font-semibold">{isEditMode ? (editedDispute.contactPhone ?? selectedDispute.contactPhone) : selectedDispute.contactPhone}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dispute Summary Panel */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    Dispute Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      Dispute ID
                    </p>
                    <p className="text-sm font-semibold">{selectedDispute.id}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <PoundSterling className="h-3 w-3" />
                      Amount
                    </p>
                    <p className="text-base font-bold text-red-600 dark:text-red-400">
                      £{selectedDispute.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Dispute Type</p>
                    <p className="text-sm font-semibold">{selectedDispute.type}</p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</p>
                    {isEditMode ? (
                      <Select
                        value={editedDispute.status || selectedDispute.status}
                        onValueChange={(value) => setEditedDispute(prev => ({ ...prev, status: value }))}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="New">New</SelectItem>
                          <SelectItem value="Under Review">Under Review</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Escalated">Escalated</SelectItem>
                          <SelectItem value="Resolved">Resolved</SelectItem>
                          <SelectItem value="Closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={cn(statusColors[derivedStatus as keyof typeof statusColors] || "bg-gray-500", "w-fit")}>
                        {derivedStatus}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Priority</p>
                    {isEditMode ? (
                      <Select
                        value={editedDispute.priority || selectedDispute.priority}
                        onValueChange={(value) => setEditedDispute(prev => ({ ...prev, priority: value }))}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Low">Low</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={cn(priorityColors[selectedDispute.priority as keyof typeof priorityColors] || "bg-gray-500", "w-fit")}>
                        {selectedDispute.priority}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <UserCheck className="h-3 w-3" />
                      Assigned To
                    </p>
                    {isEditMode ? (
                      <Select
                        value={editedDispute.assignedTo || selectedDispute.assignedTo}
                        onValueChange={(value) => setEditedDispute(prev => ({ ...prev, assignedTo: value }))}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map((user) => (
                            <SelectItem key={user.id} value={`${user.firstName} ${user.lastName}`}>
                              {`${user.firstName} ${user.lastName}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm font-semibold">{selectedDispute.assignedTo}</p>
                    )}
                  </div>
                  <div className="space-y-1.5 col-span-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <GitBranch className="h-3 w-3" />
                      Workflow Level
                    </p>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold">{selectedDispute.workflow}</span>
                        <span className="text-muted-foreground">
                          Level {selectedDispute.currentLevel} of {selectedDispute.totalLevels || getTotalLevelsForDisputeType(selectedDispute.type)}
                        </span>
                      </div>
                      <Progress 
                        value={(selectedDispute.currentLevel / (selectedDispute.totalLevels || getTotalLevelsForDisputeType(selectedDispute.type))) * 100} 
                        className="h-2" 
                      />
                    </div>
                  </div>
                </div>
                
                {/* Closed Dispute Information */}
                {selectedDispute.closedReason && (
                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <XCircle className="h-5 w-5 text-gray-600 dark:text-gray-400 mt-0.5" />
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          Dispute Closed
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Reason:</span> {getClosedReasonLabel(selectedDispute.closedReason)}
                        </p>
                        {selectedDispute.closedDate && (
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Closed on:</span> {format(new Date(selectedDispute.closedDate), 'PPP')}
                          </p>
                        )}
                        {selectedDispute.closedNote && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                            <span className="font-medium">Note:</span> {selectedDispute.closedNote}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              </CardContent>
            </Card>
          </div>

          {/* Resolution Outcome Information */}
          {selectedDispute.resolutionOutcome && (
            <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400 mt-0.5" />
                  <div className="flex-1 space-y-2">
                    <h3 className="text-base font-semibold text-green-900 dark:text-green-100">
                      Dispute Resolved
                    </h3>
                    <div className="space-y-1">
                      <p className="text-sm text-green-700 dark:text-green-300">
                        <span className="font-medium">Outcome:</span> {getResolutionOutcomeLabel(selectedDispute.resolutionOutcome)}
                      </p>
                      <p className="text-sm text-green-600 dark:text-green-400 italic">
                        {getResolutionOutcomeDescription(selectedDispute.resolutionOutcome)}
                      </p>
                      {selectedDispute.resolutionOutcome === 'partial' && selectedDispute.resolutionAmount != null && (
                        <p className="text-sm text-green-700 dark:text-green-300">
                          <span className="font-medium">Resolution Amount:</span> £{selectedDispute.resolutionAmount.toLocaleString()}
                        </p>
                      )}
                      {selectedDispute.resolutionDate && (
                        <p className="text-sm text-green-700 dark:text-green-300">
                          <span className="font-medium">Resolved on:</span> {format(new Date(selectedDispute.resolutionDate), 'PPP')}
                        </p>
                      )}
                      {selectedDispute.resolvedBy && (
                        <p className="text-sm text-green-700 dark:text-green-300">
                          <span className="font-medium">Resolved by:</span> {selectedDispute.resolvedBy}
                        </p>
                      )}
                      {selectedDispute.resolutionDetails && (
                        <p className="text-sm text-green-700 dark:text-green-300 mt-2">
                          <span className="font-medium">Details:</span> {selectedDispute.resolutionDetails}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Transactions Panel */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    Transactions Under Dispute
                  </CardTitle>
                  <CardDescription>
                    Transactions associated with this dispute
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* Search */}
                  <div className="relative w-56">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search transactions..."
                      value={txSearchQuery}
                      onChange={(e) => { setTxSearchQuery(e.target.value); setTxCurrentPage(1); }}
                      className="pl-8"
                    />
                  </div>
                  {/* Export button */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" title="Export transactions">
                        <FileDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleExportTransactions(false)}>
                        Export Visible Columns
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportTransactions(true)}>
                        Export All Columns
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {/* Column config */}
                  <ColumnConfigDialog
                    columns={txColumns}
                    onApply={handleTxColumnConfigApply}
                    onReset={resetTxColumnPreferences}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {isEditMode && <TableHead className="w-12"></TableHead>}
                      {txColumnVisibility['referenceNumber'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('referenceNumber')}>
                          <div className="flex items-center">Reference Number{renderTxSortIcon('referenceNumber')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['transactionDate'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('transactionDate')}>
                          <div className="flex items-center">Transaction Date{renderTxSortIcon('transactionDate')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['dueDate'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('dueDate')}>
                          <div className="flex items-center">Due Date{renderTxSortIcon('dueDate')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['customerName'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('customerName')}>
                          <div className="flex items-center">Customer Name{renderTxSortIcon('customerName')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['transactionType'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('transactionType')}>
                          <div className="flex items-center">Type{renderTxSortIcon('transactionType')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['currency'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('currency')}>
                          <div className="flex items-center">Currency{renderTxSortIcon('currency')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['amount'] && (
                        <TableHead className="text-right cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('amount')}>
                          <div className="flex items-center justify-end">Gross{renderTxSortIcon('amount')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['outstandingAmount'] && (
                        <TableHead className="text-right cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('outstandingAmount')}>
                          <div className="flex items-center justify-end">Outstanding{renderTxSortIcon('outstandingAmount')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['status'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('status')}>
                          <div className="flex items-center">Status{renderTxSortIcon('status')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['description'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('description')}>
                          <div className="flex items-center">Description{renderTxSortIcon('description')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['createdBy'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('createdBy')}>
                          <div className="flex items-center">Created By{renderTxSortIcon('createdBy')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['modifiedBy'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('modifiedBy')}>
                          <div className="flex items-center">Modified By{renderTxSortIcon('modifiedBy')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['accountCode'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('accountCode')}>
                          <div className="flex items-center">Account{renderTxSortIcon('accountCode')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['netAmount'] && (
                        <TableHead className="text-right cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('netAmount')}>
                          <div className="flex items-center justify-end">Net{renderTxSortIcon('netAmount')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['vatAmount'] && (
                        <TableHead className="text-right cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('vatAmount')}>
                          <div className="flex items-center justify-end">Tax{renderTxSortIcon('vatAmount')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['onDispute'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('onDispute')}>
                          <div className="flex items-center">On Dispute{renderTxSortIcon('onDispute')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['subLedger'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('subLedger')}>
                          <div className="flex items-center">Sub Ledger{renderTxSortIcon('subLedger')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['orderNumber'] && (
                        <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleTxSort('orderNumber')}>
                          <div className="flex items-center">Order Number{renderTxSortIcon('orderNumber')}</div>
                        </TableHead>
                      )}
                      {txColumnVisibility['actions'] && (
                        <TableHead>Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {txPagedTransactions.map((transaction) => {
                      const isOverdue = transaction.dueDate ? new Date(transaction.dueDate) < new Date() : false;
                      const isCompleted = transaction.status === 'completed';
                      let statusText = 'Outstanding';
                      if (isCompleted) statusText = 'Completed';
                      else if (isOverdue) statusText = 'Overdue';
                      return (
                      <TableRow
                        key={transaction.id}
                        className={cn(
                          isEditMode && "cursor-pointer hover:bg-muted/50",
                          isEditMode && selectedTxIds.has(transaction.id) && "bg-muted/30"
                        )}
                        onClick={() => isEditMode && handleToggleTx(transaction.id)}
                      >
                        {isEditMode && (
                          <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedTxIds.has(transaction.id)}
                              onCheckedChange={() => handleToggleTx(transaction.id)}
                              aria-label={`Select ${transaction.referenceNumber}`}
                            />
                          </TableCell>
                        )}
                        {txColumnVisibility['referenceNumber'] && (
                          <TableCell className="font-medium">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleViewTransactionDetail(transaction); }}
                              className="text-primary hover:underline transition-colors text-left"
                              title={`View details for ${transaction.referenceNumber}`}
                            >
                              {transaction.referenceNumber}
                            </button>
                          </TableCell>
                        )}
                        {txColumnVisibility['transactionDate'] && (
                          <TableCell>{format(new Date(transaction.date), 'dd/MM/yyyy')}</TableCell>
                        )}
                        {txColumnVisibility['dueDate'] && (
                          <TableCell>{formatDueDate(transaction.dueDate)}</TableCell>
                        )}
                        {txColumnVisibility['customerName'] && (
                          <TableCell>-</TableCell>
                        )}
                        {txColumnVisibility['transactionType'] && (
                          <TableCell><TransactionTypeBadge type={transaction.transactionType} /></TableCell>
                        )}
                        {txColumnVisibility['currency'] && (
                          <TableCell>{transaction.currency}</TableCell>
                        )}
                        {txColumnVisibility['amount'] && (
                          <TableCell className="text-right">
                            {formatCurrency(transaction.amount)}
                          </TableCell>
                        )}
                        {txColumnVisibility['outstandingAmount'] && (
                          <TableCell className="text-right font-medium">
                            {formatCurrency(transaction.amount)}
                          </TableCell>
                        )}
                        {txColumnVisibility['status'] && (
                          <TableCell>
                            <Badge variant={getTransactionStatusBadgeVariant(statusText)}>
                              {statusText}
                            </Badge>
                          </TableCell>
                        )}
                        {txColumnVisibility['description'] && (
                          <TableCell className="max-w-[200px] truncate">{transaction.description}</TableCell>
                        )}
                        {txColumnVisibility['createdBy'] && (
                          <TableCell>-</TableCell>
                        )}
                        {txColumnVisibility['modifiedBy'] && (
                          <TableCell>-</TableCell>
                        )}
                        {txColumnVisibility['accountCode'] && (
                          <TableCell>-</TableCell>
                        )}
                        {txColumnVisibility['netAmount'] && (
                          <TableCell className="text-right">-</TableCell>
                        )}
                        {txColumnVisibility['vatAmount'] && (
                          <TableCell className="text-right">-</TableCell>
                        )}
                        {txColumnVisibility['onDispute'] && (
                          <TableCell>Yes</TableCell>
                        )}
                        {txColumnVisibility['subLedger'] && (
                          <TableCell>-</TableCell>
                        )}
                        {txColumnVisibility['orderNumber'] && (
                          <TableCell>-</TableCell>
                        )}
                        {txColumnVisibility['actions'] && (
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewTransactionDetail(transaction); }}>
                              View
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );})}
                    {txPagedTransactions.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={(Object.values(txColumnVisibility).filter(Boolean).length || 7) + (isEditMode ? 1 : 0)}
                          className="text-center py-8 text-muted-foreground"
                        >
                          {txSearchQuery ? (
                            <>No transactions found matching &quot;{txSearchQuery}&quot;</>
                          ) : (
                            <div className="flex flex-col items-center gap-2">
                              <Receipt className="h-10 w-10 opacity-40" />
                              <span>No transactions found</span>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {txTotalItems > 0 && (
                <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  {/* Rows per page + count */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <Select
                      value={txItemsPerPage.toString()}
                      onValueChange={(value) => { setTxItemsPerPage(Number(value)); setTxCurrentPage(1); }}
                    >
                      <SelectTrigger className="w-[80px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">
                      Showing {((txCurrentPage - 1) * txItemsPerPage) + 1}–{Math.min(txCurrentPage * txItemsPerPage, txTotalItems)} of {txTotalItems} transactions
                    </span>
                  </div>

                  {/* Page navigation */}
                  {txTotalPages > 1 && (
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => setTxCurrentPage((prev) => Math.max(1, prev - 1))}
                            className={cn(txCurrentPage === 1 && 'pointer-events-none opacity-50', 'cursor-pointer')}
                          />
                        </PaginationItem>
                        {getTxPageNumbers().map((page, index) => (
                          <PaginationItem key={`${page}-${index}`}>
                            {typeof page === 'number' ? (
                              <PaginationLink
                                onClick={() => setTxCurrentPage(page)}
                                isActive={txCurrentPage === page}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            ) : (
                              <PaginationEllipsis />
                            )}
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => setTxCurrentPage((prev) => Math.min(txTotalPages, prev + 1))}
                            className={cn(txCurrentPage === txTotalPages && 'pointer-events-none opacity-50', 'cursor-pointer')}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  )}
                </div>
              )}

              <Separator className="my-4" />
              <div className="flex justify-between items-center">
                <span className="font-semibold">
                  {isEditMode
                    ? `Total Disputed Amount (${selectedTxIds.size} of ${transactionList.length} selected):`
                    : "Total Disputed Amount:"}
                </span>
                <span className="text-xl font-bold text-red-600 dark:text-red-400">
                  {isEditMode
                    ? formatCurrency(transactionList.filter(t => selectedTxIds.has(t.id)).reduce((sum, t) => sum + (t.amount ?? 0), 0))
                    : formatCurrency(transactionList.reduce((sum, t) => sum + (t.amount ?? 0), 0))}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Dispute Notes Panel */}
          <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-green-600 dark:text-green-400" />
                    Dispute Notes
                    <Badge variant="outline" className="ml-2">
                      {disputeNotes.length} notes
                    </Badge>
                </CardTitle>
              </CardHeader>
                <CardContent>
                  {disputeNotes.length > 0 ? (
                    <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                      {disputeNotes.map((note) => (
                        <div key={note.id} className="border-b pb-3 last:border-b-0 last:pb-0">
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                            {note.content}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                            <User className="h-3 w-3" />
                            <span>{note.createdBy}</span>
                            <span>•</span>
                            <Clock className="h-3 w-3" />
                            <span>{format(note.createdAt, "MMM dd, yyyy 'at' h:mm a")}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                      <p className="text-sm text-muted-foreground">No notes yet</p>
                    </div>
                  )}
                </CardContent>
            </Card>
        </TabsContent>

        {/* Tab 2: Workflow */}
        <TabsContent value="workflow" className="space-y-4">
          {/* Workflow Progress Card */}
          {(() => {
            const baseDate = new Date(selectedDispute.createdDate);
            const stagesWithDates = workflowStages.map((stage, index) => ({
              ...stage,
              completedDate: stage.level <= selectedDispute.currentLevel 
                ? new Date(baseDate.getTime() + index * 24 * 60 * 60 * 1000).toISOString()
                : null,
            }));

            return (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <GitBranch className="h-5 w-5 text-primary" />
                    Workflow Progress
                  </CardTitle>
                  <CardDescription>{selectedDispute.type} Resolution Workflow</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 mb-6">
                    <span className="text-sm font-medium">Current Level:</span>
                    <Badge>{selectedDispute.currentLevel} of {workflowStages.length}</Badge>
                  </div>

                  <div className="space-y-6">
                    {stagesWithDates.map((step, index) => (
                      <div key={step.level} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                            step.level <= selectedDispute.currentLevel
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground"
                          }`}>
                            {renderStepIcon(step.level, selectedDispute.currentLevel)}
                          </div>
                          {index < workflowStages.length - 1 && (
                            <div className={`w-0.5 h-12 ${
                              step.level < selectedDispute.currentLevel
                                ? "bg-primary"
                                : "bg-muted"
                            }`} />
                          )}
                        </div>
                        <div className="flex-1 pb-8">
                          <h4 className="font-semibold">Level {step.level}</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            {step.description}
                          </p>
                          {step.completedDate && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Completed: {format(new Date(step.completedDate), "MMM dd, yyyy")}
                            </p>
                          )}
                          {Boolean(step.estimatedDays) && !step.completedDate && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Estimated: {step.estimatedDays} day{step.estimatedDays !== 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })()}
        </TabsContent>

        {/* Tab 3: Activity */}
        <TabsContent value="activity" className="space-y-4">
          <ConsolidatedActivityTimeline
            activities={activities}
            communications={mockCommunications}
            notes={disputeNotes}
            isEditMode={isEditMode}
            onAddNote={async (content: string) => {
              console.log("Adding note:", content);
            }}
          />
        </TabsContent>

        {/* Tab 4: Documents */}
        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Documents
              </CardTitle>
              <CardDescription>
                Upload and manage dispute-related documents
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button className="w-full" onClick={handleUploadDocument}>
                <Plus className="mr-2 h-4 w-4" />
                Upload Document
              </Button>
              <div className="space-y-3">
                {documents.map((doc) => (
                  <Card key={`${doc.name}-${doc.date}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="h-8 w-8 text-muted-foreground" />
                          <div>
                            <p className="font-medium text-sm">{doc.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {doc.size} • Uploaded {format(new Date(doc.date), "MMM dd, yyyy")}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 5: Audit Log */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Audit Log
              </CardTitle>
              <CardDescription>
                Complete chronological history of all dispute modifications and changes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {auditLogs.map((log, index) => {
                  const Icon = getAuditLogIcon(log.action);
                  const colorClass = getAuditLogColor(log.action);
                  
                  return (
                    <div key={log.id} className="relative">
                      {index !== auditLogs.length - 1 && (
                        <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                      )}
                      <div className="flex gap-4">
                        <div className={cn(
                          "flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0",
                          colorClass
                        )}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 space-y-1">
                              <p className="font-medium text-sm">{log.description}</p>
                              
                              {/* Status/Priority/Field Changes */}
                              {(log.action === "status_change" || log.action === "priority_change" || log.action === "field_update" || log.action === "assignment_change") && log.oldValue && (
                                <div className="flex items-center gap-2 text-sm">
                                  <Badge variant="outline" className="text-xs">
                                    {log.oldValue}
                                  </Badge>
                                  <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
                                  <Badge variant="outline" className="text-xs">
                                    {log.newValue}
                                  </Badge>
                                </div>
                              )}
                              
                              {/* Note Added */}
                              {log.action === "note_added" && log.newValue && (
                                <div className="text-sm text-muted-foreground">
                                  <p className="italic">&quot;{log.newValue}&quot;</p>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground whitespace-nowrap">
                              {format(log.createdAt, "MMM d, yyyy h:mm a")}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              <User className="h-3 w-3 mr-1" />
                              {log.createdBy}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {auditLogs.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No audit log entries found for this dispute.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Log Call Modal */}
      <LogCallModal
        open={logCallOpen}
        onOpenChange={setLogCallOpen}
        customerName={selectedDispute?.customer || ""}
        customerPhone={selectedDispute?.contactPhone}
        disputeId={selectedDisputeNumericId ? selectedDisputeNumericId.toString() : undefined}
      />

      {/* Send Email Modal */}
      <SendEmailModal
        open={sendEmailOpen}
        onOpenChange={setSendEmailOpen}
        customerName={selectedDispute?.customer || ""}
        customerEmail={selectedDispute?.contactEmail || ""}
        customerId={selectedDispute?.customerId?.toString()}
        sourceType="dispute"
        sourceId={selectedDisputeNumericId ? selectedDisputeNumericId.toString() : undefined}
      />

      {/* Next Action Modal */}
      <Dialog open={isNextActionModalOpen} onOpenChange={setIsNextActionModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              Proceed to Next Stage
            </DialogTitle>
            <DialogDescription>
              Complete the current stage and advance the dispute workflow
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Next Stage Info */}
            {selectedDispute && (() => {
              const currentStage = workflowStages.find(s => s.level === selectedDispute.currentLevel);
              
              return currentStage ? (
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950 p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <p className="font-semibold text-sm text-blue-900 dark:text-blue-100">
                      Current Stage: {currentStage.title}
                    </p>
                  </div>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    {currentStage.description}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Level {selectedDispute.currentLevel} of {selectedDispute.totalLevels}
                  </p>
                </div>
              ) : null;
            })()}

            {/* Action Notes */}
            <div className="space-y-2">
              <Label htmlFor="action-notes" className="flex items-center gap-1">
                Action Notes{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="action-notes"
                placeholder="Enter notes about the actions taken and any relevant details..."
                value={nextActionNotes}
                onChange={(e) => setNextActionNotes(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                These notes will be added to the activity timeline
              </p>
            </div>

            {/* Follow-up Date */}
            <div className="space-y-2">
              <Label htmlFor="follow-up-date">Follow-up Date (Optional)</Label>
              <Input
                id="follow-up-date"
                type="date"
                value={nextActionFollowUpDate}
                onChange={(e) => setNextActionFollowUpDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">
                Set a reminder for the next action
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelNextAction}
            >
              Cancel
            </Button>
            <Button
              onClick={handleProceedToNextStage}
              disabled={!nextActionNotes.trim() || updateDisputeMutation.isPending}
              className="gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Modal */}
      <AddDisputeNoteModal
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        onSave={handleSaveNote}
        disputeId={disputeId || ''}
      />

      {/* Upload Document Modal */}
      <UploadDocumentModal
        isOpen={isUploadDocumentModalOpen}
        onClose={() => setIsUploadDocumentModalOpen(false)}
        onUpload={handleDocumentUpload}
        disputeId={disputeId || ''}
      />

      {/* Resolve Dispute Modal */}
      <Dialog open={isResolveDisputeModalOpen} onOpenChange={setIsResolveDisputeModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Complete Resolution
            </DialogTitle>
            <DialogDescription>
              {selectedDispute && selectedDispute.currentLevel < selectedDispute.totalLevels - 1 && (
                <span className="text-amber-600 font-medium">
                  ⚠️ Early Resolution - You are resolving at Level {selectedDispute.currentLevel} of {selectedDispute.totalLevels}
                </span>
              )}
              {selectedDispute && selectedDispute.currentLevel === selectedDispute.totalLevels - 1 && (
                <span>You are at Level {selectedDispute.currentLevel} of {selectedDispute.totalLevels}</span>
              )}
              {selectedDispute && selectedDispute.currentLevel === selectedDispute.totalLevels && (
                <span>You are at the final workflow level ({selectedDispute.totalLevels} of {selectedDispute.totalLevels})</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Resolution Outcome */}
            <div className="space-y-2">
              <Label>Resolution Outcome *</Label>
              <RadioGroup value={resolutionOutcome} onValueChange={(value: any) => setResolutionOutcome(value)}>
                <div className="flex items-start space-x-2 rounded-lg border p-3 hover:bg-accent">
                  <RadioGroupItem value="upheld" id="upheld" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="upheld" className="font-medium cursor-pointer">
                      Upheld (Customer Won)
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      The customer's dispute is valid. Transaction will be reversed.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-2 rounded-lg border p-3 hover:bg-accent">
                  <RadioGroupItem value="denied" id="denied" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="denied" className="font-medium cursor-pointer">
                      Denied (Merchant Won)
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Dispute is not valid. Transaction stands as is.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-2 rounded-lg border p-3 hover:bg-accent">
                  <RadioGroupItem value="partial" id="partial" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="partial" className="font-medium cursor-pointer">
                      Partial Resolution
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Partial credit issued to customer.
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Resolution Amount (only for partial) */}
            {resolutionOutcome === 'partial' && (
              <div className="space-y-2">
                <Label htmlFor="resolutionAmount">Resolution Amount *</Label>
                <Input
                  id="resolutionAmount"
                  type="number"
                  step="0.01"
                  placeholder="Enter amount (e.g., 2500.00)"
                  value={resolutionAmount}
                  onChange={(e) => setResolutionAmount(e.target.value)}
                />
              </div>
            )}

            {/* Resolution Notes */}
            <div className="space-y-2">
              <Label htmlFor="resolutionNotes">Resolution Notes *</Label>
              <Textarea
                id="resolutionNotes"
                placeholder="Explain the resolution reasoning and any actions taken..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Provide detailed notes about why this resolution was reached and any relevant context.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsResolveDisputeModalOpen(false);
                setResolutionOutcome('upheld');
                setResolutionAmount('');
                setResolutionNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResolveDispute}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Complete Resolution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Dispute Modal */}
      <Dialog open={isCloseDisputeModalOpen} onOpenChange={setIsCloseDisputeModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Close Dispute</DialogTitle>
            <DialogDescription>
              Close this dispute without completing all workflow steps. This action will mark the dispute as closed and remove it from active views.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="close-reason">Reason for Closing</Label>
              <Select value={closeReason} onValueChange={(value: any) => setCloseReason(value)}>
                <SelectTrigger id="close-reason">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="duplicate">Duplicate Entry</SelectItem>
                  <SelectItem value="customer_withdrew">Customer Withdrew Dispute</SelectItem>
                  <SelectItem value="invalid_dispute">Invalid Dispute</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="close-note">Additional Notes {closeReason === 'other' && <span className="text-destructive">*</span>}</Label>
              <Textarea
                id="close-note"
                placeholder="Provide additional context for closing this dispute..."
                value={closeNote}
                onChange={(e) => setCloseNote(e.target.value)}
                rows={4}
              />
              {closeReason === 'other' && !closeNote && (
                <p className="text-sm text-muted-foreground">Note is required when selecting "Other" as the reason.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCloseDisputeModalOpen(false);
                setCloseReason('duplicate');
                setCloseNote('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleCloseDispute}
              disabled={closeReason === 'other' && !closeNote}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Close Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Dispute Modal */}
      <Dialog open={isReopenDisputeModalOpen} onOpenChange={setIsReopenDisputeModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Reopen Dispute</DialogTitle>
            <DialogDescription>
              Reopen this closed dispute to continue investigation. The dispute will return to its previous workflow level.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reopen-reason">Reason for Reopening <span className="text-destructive">*</span></Label>
              <Textarea
                id="reopen-reason"
                placeholder="Explain why this dispute needs to be reopened..."
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                rows={4}
              />
              <p className="text-sm text-muted-foreground">
                Previous closure reason: {selectedDispute?.closedReason ? getClosedReasonLabel(selectedDispute.closedReason) : 'Unknown'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsReopenDisputeModalOpen(false);
                setReopenReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReopenDispute}
              disabled={!reopenReason.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reopen Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction Detail Sheet */}
      <TransactionDetailSheet
        open={transactionDetailSheetOpen}
        onOpenChange={setTransactionDetailSheetOpen}
        transaction={selectedTransactionDetail}
      />

      {/* Save Confirmation — shown when transactions have been unchecked */}
      <AlertDialog open={showSaveConfirm} onOpenChange={setShowSaveConfirm}>
        <AlertDialogContent className="sm:max-w-[520px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <span className="text-amber-500">⚠</span> Confirm Save Changes
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  You are about to save changes to this dispute. The following{" "}
                  <span className="font-semibold text-foreground">
                    {removedTransactions.length} transaction{removedTransactions.length !== 1 ? "s" : ""}
                  </span>{" "}
                  will be <span className="font-semibold text-destructive">removed</span> from the dispute:
                </p>
                <ul className="rounded-md border bg-muted/40 px-4 py-2 space-y-1 max-h-48 overflow-y-auto">
                  {removedTransactions.map(tx => (
                    <li key={tx.id} className="flex items-center justify-between gap-4 py-0.5">
                      <span className="font-medium text-foreground">{tx.referenceNumber}</span>
                      <span className="text-muted-foreground truncate max-w-[200px]">{tx.description}</span>
                      <span className="shrink-0 font-medium text-foreground">
                        {tx.currency} {formatCurrency(tx.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground">
                  {selectedTxIds.size} of {editedTransactions.length} transaction{editedTransactions.length !== 1 ? "s" : ""} will remain linked to this dispute.
                </p>
                <p>This action cannot be undone. Are you sure you want to continue?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowSaveConfirm(false)}>
              Go Back
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleConfirmSave()}
            >
              Confirm &amp; Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

        </div>
      </div>
    </div>
  </div>
  );
}
